import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { compressImage } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Send, Paperclip, X, MessageSquarePlus, Search, ImageIcon, Shield, Users, User,
} from "lucide-react";

const ROLE_PILL = {
  super_admin:     { label: "Super Admin",   color: "bg-rose-50 text-rose-700",    Icon: Shield },
  sub_super_admin: { label: "Sub Admin",     color: "bg-rose-50 text-rose-700",    Icon: Shield },
  school_owner:    { label: "Owner",         color: "bg-violet-50 text-violet-700",Icon: User },
  sub_owner:       { label: "Sub-Owner",     color: "bg-violet-50 text-violet-700",Icon: User },
  branch_admin:    { label: "Branch Admin",  color: "bg-sky-50 text-sky-700",      Icon: Users },
  special_access:  { label: "Staff",         color: "bg-sky-50 text-sky-700",      Icon: Users },
};

const POLL_MESSAGES_MS = 15000;
const POLL_THREADS_MS = 30000;

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function Avatar({ name, role }) {
  const initials = (name || "?").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const pill = ROLE_PILL[role] || ROLE_PILL.school_owner;
  return (
    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-sm ${pill.color}`}>
      {initials || "?"}
    </div>
  );
}

function RolePill({ role }) {
  const pill = ROLE_PILL[role];
  if (!pill) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${pill.color}`}>
      <pill.Icon className="w-3 h-3" /> {pill.label}
    </span>
  );
}

function HighlightedBody({ text, query }) {
  if (!query || !query.trim()) return <>{text}</>;
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${safe})`, "ig"));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="bg-yellow-200 text-foreground rounded px-0.5">{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  );
}

export default function HelpSupportPage() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [contactPage, setContactPage] = useState(1);
  const [contactHasMore, setContactHasMore] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageSearch, setMessageSearch] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [composer, setComposer] = useState("");
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, mime, name }
  const [sending, setSending] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [search, setSearch] = useState("");
  const [threadSearchMode, setThreadSearchMode] = useState("conversations"); // "conversations" | "messages"
  const [globalSearchResults, setGlobalSearchResults] = useState({ groups: [], total_matches: 0 });
  const [loadingGlobalSearch, setLoadingGlobalSearch] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const res = await api.get("/chat/threads");
      setThreads(res.data?.items || []);
    } catch { /* ignore */ }
  }, []);

  const loadContacts = useCallback(async ({ q = "", page = 1, append = false } = {}) => {
    setLoadingContacts(true);
    try {
      const res = await api.get("/chat/contacts", { params: { q: q || undefined, page, page_size: 50 } });
      const items = res.data?.items || [];
      setContacts((prev) => (append ? [...prev, ...items] : items));
      setContactPage(res.data?.page || page);
      setContactHasMore((res.data?.page || page) < (res.data?.total_pages || 1));
    } catch { /* ignore */ }
    finally { setLoadingContacts(false); }
  }, []);

  const loadMessages = useCallback(async (threadId, silent = false, q = "") => {
    if (!threadId) return;
    if (!silent) setLoadingMessages(true);
    try {
      const params = { page: 1, page_size: 100 };
      if (q && q.trim()) params.q = q.trim();
      const res = await api.get(`/chat/threads/${threadId}/messages`, { params });
      setMessages(res.data?.items || []);
    } catch { /* ignore */ }
    finally { if (!silent) setLoadingMessages(false); }
  }, []);

  const markActiveRead = useCallback(async (threadId) => {
    if (!threadId) return;
    try { await api.post(`/chat/threads/${threadId}/read`); }
    catch { /* ignore */ }
  }, []);

  // initial threads load only — contacts load lazily on modal open / search.
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  // Lazy contact load + debounced search when modal is open
  useEffect(() => {
    if (!showNewModal) return;
    const id = setTimeout(() => { loadContacts({ q: contactSearch, page: 1 }); }, 250);
    return () => clearTimeout(id);
  }, [showNewModal, contactSearch, loadContacts]);

  // poll threads
  useEffect(() => {
    const id = setInterval(loadThreads, POLL_THREADS_MS);
    return () => clearInterval(id);
  }, [loadThreads]);

  // poll active thread messages + mark read (skipped during message search)
  useEffect(() => {
    if (!activeThreadId) return;
    if (searchMode) return;
    loadMessages(activeThreadId, false);
    markActiveRead(activeThreadId).then(() => loadThreads());
    const id = setInterval(() => {
      loadMessages(activeThreadId, true);
      markActiveRead(activeThreadId).then(() => loadThreads());
    }, POLL_MESSAGES_MS);
    return () => clearInterval(id);
  }, [activeThreadId, loadMessages, markActiveRead, loadThreads, searchMode]);

  // Debounced server-side search inside the active thread
  useEffect(() => {
    if (!activeThreadId || !searchMode) return;
    const id = setTimeout(() => { loadMessages(activeThreadId, false, messageSearch); }, 250);
    return () => clearTimeout(id);
  }, [activeThreadId, searchMode, messageSearch, loadMessages]);

  // Reset search when switching threads
  useEffect(() => {
    setSearchMode(false);
    setMessageSearch("");
  }, [activeThreadId]);

  // Debounced global cross-thread message search
  useEffect(() => {
    if (threadSearchMode !== "messages") {
      setGlobalSearchResults({ groups: [], total_matches: 0 });
      return;
    }
    if (!search.trim()) {
      setGlobalSearchResults({ groups: [], total_matches: 0 });
      return;
    }
    setLoadingGlobalSearch(true);
    const id = setTimeout(async () => {
      try {
        const res = await api.get("/chat/search", { params: { q: search.trim() } });
        setGlobalSearchResults({
          groups: res.data?.groups || [],
          total_matches: res.data?.total_matches || 0,
        });
      } catch { /* ignore */ }
      finally { setLoadingGlobalSearch(false); }
    }, 250);
    return () => clearTimeout(id);
  }, [threadSearchMode, search]);

  // Open a thread from a global-search hit + pre-fill the in-thread search
  const openHit = (threadId, queryStr) => {
    setActiveThreadId(threadId);
    if (queryStr) {
      // Defer so the thread-reset effect runs first, then we set the search mode.
      setTimeout(() => {
        setSearchMode(true);
        setMessageSearch(queryStr);
      }, 50);
    }
    setThreadSearchMode("conversations");
    setSearch("");
  };

  // auto-scroll to bottom on message change (but NOT during message search,
  // where the user is browsing historical hits)
  useEffect(() => {
    if (searchMode) return;
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, activeThreadId, searchMode]);

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeThreadId) || null,
    [threads, activeThreadId]
  );

  const openContact = async (contactId) => {
    try {
      const res = await api.post("/chat/threads", { other_user_id: contactId });
      setShowNewModal(false);
      setActiveThreadId(res.data?.id || null);
      await loadThreads();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick a JPG, PNG, or WebP image"); return; }
    try {
      const dataUrl = await compressImage(f, { maxKB: 400, maxDim: 1600 });
      setPendingImage({ dataUrl, mime: "image/jpeg", name: f.name });
    } catch (err) {
      toast.error(err.message || "Could not process image");
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!activeThreadId) return;
    const body = composer.trim();
    if (!body && !pendingImage) return;
    setSending(true);
    try {
      const payload = { body: body || null };
      if (pendingImage) {
        payload.attachment_base64 = pendingImage.dataUrl;
        payload.attachment_mime = "image/jpeg";
        payload.attachment_name = pendingImage.name;
      }
      const res = await api.post(`/chat/threads/${activeThreadId}/messages`, payload);
      setComposer("");
      setPendingImage(null);
      // Append the new message locally for instant feedback; the next poll will reconcile.
      setMessages((arr) => [...arr, res.data]);
      await loadThreads();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const filteredThreads = useMemo(() => {
    if (!search.trim()) return threads;
    const q = search.toLowerCase();
    return threads.filter((t) =>
      (t.other_user_name || "").toLowerCase().includes(q) ||
      (t.other_user_email || "").toLowerCase().includes(q)
    );
  }, [threads, search]);

  const filteredContacts = contacts; // server already filtered + paginated

  return (
    <>
      <div className="space-y-4" data-testid="help-support-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Help & Support</h1>
            <p className="text-sm text-brand-inkMuted mt-1">Message your team directly. Send screenshots, get answers, resolve issues fast.</p>
          </div>
          <Button onClick={() => setShowNewModal(true)} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="chat-new-thread-btn">
            <MessageSquarePlus className="w-4 h-4 mr-2" /> New conversation
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 h-[70vh] min-h-[480px]">
          {/* Threads list */}
          <aside className="bg-white rounded-3xl border border-[#2563EB]/10 flex flex-col overflow-hidden" data-testid="chat-threads-pane">
            <div className="p-3 border-b border-slate-100 space-y-2">
              <div className="flex gap-1 p-0.5 bg-slate-100 rounded-full text-xs font-semibold" data-testid="chat-search-tabs">
                <button
                  type="button"
                  onClick={() => setThreadSearchMode("conversations")}
                  className={`flex-1 px-3 py-1.5 rounded-full transition-colors ${threadSearchMode === "conversations" ? "bg-white shadow-sm text-[#1D4ED8]" : "text-slate-600 hover:text-slate-800"}`}
                  data-testid="chat-search-tab-conversations"
                >Conversations</button>
                <button
                  type="button"
                  onClick={() => setThreadSearchMode("messages")}
                  className={`flex-1 px-3 py-1.5 rounded-full transition-colors ${threadSearchMode === "messages" ? "bg-white shadow-sm text-[#1D4ED8]" : "text-slate-600 hover:text-slate-800"}`}
                  data-testid="chat-search-tab-messages"
                >Messages</button>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={threadSearchMode === "messages" ? "Search across all conversations" : "Search conversations"}
                  className="pl-9 rounded-full"
                  data-testid="chat-search-input"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {threadSearchMode === "messages" ? (
                <div data-testid="chat-global-search-results">
                  {!search.trim() && (
                    <div className="p-6 text-center text-sm text-brand-inkMuted">
                      Type to search across every conversation you're part of.
                    </div>
                  )}
                  {search.trim() && loadingGlobalSearch && (
                    <div className="p-6 text-center text-sm text-brand-inkMuted">Searching…</div>
                  )}
                  {search.trim() && !loadingGlobalSearch && globalSearchResults.groups.length === 0 && (
                    <div className="p-6 text-center text-sm text-brand-inkMuted">
                      No messages match "{search.trim()}".
                    </div>
                  )}
                  {search.trim() && !loadingGlobalSearch && globalSearchResults.groups.length > 0 && (
                    <>
                      <div className="px-4 py-2 text-[11px] uppercase tracking-wide font-semibold text-brand-inkMuted bg-slate-50/80">
                        {globalSearchResults.total_matches} match{globalSearchResults.total_matches === 1 ? "" : "es"} in {globalSearchResults.groups.length} conversation{globalSearchResults.groups.length === 1 ? "" : "s"}
                      </div>
                      {globalSearchResults.groups.map((g) => (
                        <div key={g.thread.id} className="border-b border-slate-100" data-testid={`chat-global-group-${g.thread.id}`}>
                          <div className="flex items-center gap-2 px-4 py-2 bg-slate-50/40">
                            <Avatar name={g.thread.other_user_name} role={g.thread.other_user_role} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm truncate">{g.thread.other_user_name}</span>
                                <RolePill role={g.thread.other_user_role} />
                              </div>
                              <div className="text-[10px] text-brand-inkMuted">{g.match_count} match{g.match_count === 1 ? "" : "es"}</div>
                            </div>
                          </div>
                          {g.matches.slice(0, 3).map((m) => (
                            <button
                              type="button"
                              key={m.id}
                              onClick={() => openHit(g.thread.id, search.trim())}
                              className="w-full text-left px-4 py-2.5 hover:bg-[#EFF6FF] transition-colors border-t border-slate-50"
                              data-testid={`chat-global-hit-${m.id}`}
                            >
                              <div className="text-[11px] text-slate-500 mb-0.5">
                                {m.sender_name} • {fmtTime(m.created_at)}
                              </div>
                              <div className="text-sm text-foreground line-clamp-2">
                                <HighlightedBody text={m.body || ""} query={search.trim()} />
                              </div>
                            </button>
                          ))}
                          {g.match_count > 3 && (
                            <button
                              type="button"
                              onClick={() => openHit(g.thread.id, search.trim())}
                              className="w-full text-left px-4 py-2 text-xs text-[#1D4ED8] hover:bg-[#EFF6FF] border-t border-slate-50"
                            >
                              +{g.match_count - 3} more match{g.match_count - 3 === 1 ? "" : "es"} in this conversation
                            </button>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <>
                  {filteredThreads.length === 0 && (
                    <div className="p-6 text-center text-sm text-brand-inkMuted">
                      No conversations yet. Tap <strong>New conversation</strong> to start.
                    </div>
                  )}
                  {filteredThreads.map((t) => {
                    const active = t.id === activeThreadId;
                    return (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => setActiveThreadId(t.id)}
                        className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 transition-colors ${active ? "bg-[#EFF6FF]" : "hover:bg-slate-50"}`}
                        data-testid={`chat-thread-${t.id}`}
                      >
                        <Avatar name={t.other_user_name} role={t.other_user_role} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-foreground truncate">{t.other_user_name || "Unnamed"}</span>
                            {t.unread_count > 0 && (
                              <span className="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`chat-unread-${t.id}`}>
                                {t.unread_count}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-brand-inkMuted truncate">{t.last_message_preview || "No messages yet"}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {fmtTime(t.last_message_at) || fmtTime(t.created_at)}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </aside>

          {/* Messages pane */}
          <section className="bg-white rounded-3xl border border-[#2563EB]/10 flex flex-col overflow-hidden" data-testid="chat-messages-pane">
            {!activeThread ? (
              <div className="flex-1 flex items-center justify-center p-8 text-center">
                <div>
                  <MessageSquarePlus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-brand-inkMuted">Pick a conversation on the left, or start a new one.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
                  <Avatar name={activeThread.other_user_name} role={activeThread.other_user_role} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-foreground truncate">{activeThread.other_user_name}</span>
                      <RolePill role={activeThread.other_user_role} />
                    </div>
                    <p className="text-xs text-brand-inkMuted truncate">{activeThread.other_user_email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSearchMode((v) => !v)}
                    className={`shrink-0 rounded-full p-2 transition-colors ${searchMode ? "bg-[#EFF6FF] text-[#1D4ED8]" : "text-slate-500 hover:bg-slate-100"}`}
                    title={searchMode ? "Close search" : "Search messages"}
                    data-testid="chat-message-search-toggle"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>

                {searchMode && (
                  <div className="px-5 py-3 border-b border-slate-100 bg-[#EFF6FF]/40" data-testid="chat-message-search-bar">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={messageSearch}
                        onChange={(e) => setMessageSearch(e.target.value)}
                        placeholder="Search this conversation…"
                        className="pl-9 pr-10 rounded-full bg-white"
                        autoFocus
                        data-testid="chat-message-search-input"
                      />
                      {messageSearch && (
                        <button
                          type="button"
                          onClick={() => setMessageSearch("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-slate-100"
                          data-testid="chat-message-search-clear"
                        >
                          <X className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                      )}
                    </div>
                    <div className="text-[11px] text-brand-inkMuted mt-2">
                      {messageSearch.trim()
                        ? `${messages.length} match${messages.length === 1 ? "" : "es"} for "${messageSearch.trim()}"`
                        : "Type to search this thread's messages"}
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/40" ref={scrollRef}>
                  {loadingMessages && messages.length === 0 && (
                    <div className="text-center text-sm text-brand-inkMuted py-8">Loading…</div>
                  )}
                  {!loadingMessages && messages.length === 0 && (
                    <div className="text-center text-sm text-brand-inkMuted py-8">
                      {searchMode && messageSearch.trim()
                        ? `No messages matching "${messageSearch.trim()}".`
                        : `Say hi 👋 — this is the start of your conversation with ${activeThread.other_user_name}.`}
                    </div>
                  )}
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                          {!mine && (
                            <div className="text-[10px] text-slate-500 ml-3 mb-1">{m.sender_name}</div>
                          )}
                          <div className={`rounded-3xl px-4 py-2.5 shadow-sm ${mine ? "bg-[#2563EB] text-white rounded-br-md" : "bg-white border border-slate-200 text-foreground rounded-bl-md"}`}>
                            {m.attachment && m.attachment.base64 && (
                              <a
                                href={m.attachment.base64.startsWith("data:") ? m.attachment.base64 : `data:${m.attachment.mime};base64,${m.attachment.base64}`}
                                target="_blank" rel="noreferrer"
                                className="block mb-1.5"
                              >
                                <img
                                  src={m.attachment.base64.startsWith("data:") ? m.attachment.base64 : `data:${m.attachment.mime};base64,${m.attachment.base64}`}
                                  alt={m.attachment.name || "Attachment"}
                                  className="rounded-2xl max-h-60 object-cover"
                                />
                              </a>
                            )}
                            {m.body && (
                              <div className="text-sm whitespace-pre-wrap break-words">
                                <HighlightedBody text={m.body} query={searchMode ? messageSearch : ""} />
                              </div>
                            )}
                          </div>
                          <div className={`text-[10px] mt-1 ${mine ? "text-slate-400 mr-2" : "text-slate-400 ml-3"}`}>{fmtTime(m.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <form onSubmit={sendMessage} className="border-t border-slate-100 p-3 space-y-2">
                  {pendingImage && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-2xl" data-testid="chat-pending-image">
                      <ImageIcon className="w-4 h-4 text-slate-500" />
                      <span className="text-xs truncate flex-1">{pendingImage.name}</span>
                      <button type="button" onClick={() => setPendingImage(null)} className="text-slate-500 hover:text-rose-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="shrink-0 rounded-full p-2 text-slate-500 hover:bg-slate-100"
                      title="Attach image"
                      data-testid="chat-attach-btn"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPickFile} data-testid="chat-file-input" />
                    <Textarea
                      value={composer}
                      onChange={(e) => setComposer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                      }}
                      placeholder="Type a message… (Shift+Enter for new line)"
                      rows={1}
                      className="resize-none rounded-2xl"
                      data-testid="chat-composer-textarea"
                    />
                    <Button type="submit" disabled={sending || (!composer.trim() && !pendingImage)} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="chat-send-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            )}
          </section>
        </div>

        {/* New conversation modal */}
        {showNewModal && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowNewModal(false)} data-testid="chat-new-modal">
            <div className="bg-white rounded-3xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <h2 className="font-heading font-bold">New conversation</h2>
                <button type="button" onClick={() => setShowNewModal(false)} className="rounded-full p-1 hover:bg-slate-100"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-3 border-b border-slate-100">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder="Search by name or email"
                    className="pl-9 rounded-full"
                    data-testid="chat-new-search-input"
                    autoFocus
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                {loadingContacts && filteredContacts.length === 0 && (
                  <div className="p-6 text-center text-sm text-brand-inkMuted">Loading…</div>
                )}
                {!loadingContacts && filteredContacts.length === 0 && (
                  <div className="p-6 text-center text-sm text-brand-inkMuted">
                    No contacts found{contactSearch ? ` for "${contactSearch}"` : ""}.
                  </div>
                )}
                {filteredContacts.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => openContact(c.id)}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                    data-testid={`chat-contact-${c.id}`}
                  >
                    <Avatar name={c.name} role={c.role} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm truncate">{c.name}</span>
                        <RolePill role={c.role} />
                      </div>
                      <p className="text-xs text-brand-inkMuted truncate">{c.email}</p>
                    </div>
                  </button>
                ))}
                {contactHasMore && (
                  <button
                    type="button"
                    onClick={() => loadContacts({ q: contactSearch, page: contactPage + 1, append: true })}
                    disabled={loadingContacts}
                    className="w-full text-center text-sm font-semibold text-[#1D4ED8] py-3 hover:bg-[#EFF6FF] disabled:opacity-60"
                    data-testid="chat-contacts-load-more"
                  >
                    {loadingContacts ? "Loading…" : "Load more"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
